import { forwardRef, useCallback, useEffect, useState } from 'react';

import { useSwingThreadMessagesSubscription, useToggle } from '@hooks';
import {
    useDeleteSwingThread,
    useDeleteThreadMessage,
    useSendMessage,
    useUpsertSwingThread,
    useMarkNotificationAsRead,
    useThreadMessageUnreadNotifications,
} from '@queries';
import {
    colors,
    CircularCloseIcon,
    SwingThreadContainer,
    SwingThreadSelectOption,
    SwingThreadForm,
    SwingThreadMessages,
    MessageProps,
    CaretLeftIcon,
    HandleVerticalIcon,
    Box,
    u,
} from '@ui';

import { ConfirmDeleteThreadModal } from './ConfirmDeleteThreadModal/ConfirmDeleteThreadModal';

type SwingThreadMessageDeletePayload = {
    id: number;
    threadId: number;
};

type SwingThreadSubmitPayload = { nodeId?: string; message: string; messageId?: number };

type SwingThreadMessageSubmitPayload = { message: string; id?: number; threadId: number };

/**
 *  @todo Think about removing created_by privileges that can update messages as well
 */

export type SwingThreadProps = {
    threadId?: number;
    title?: string;
    initialPositionX?: number;
    initialPositionY?: number;
    currentUserId?: string;
    swingUuid?: string;
    options: SwingThreadSelectOption[];
    messages?: MessageProps[];
    nodeId?: string | null;
    fromFrame: number;
    activeUserId: string;
    onPostUpsertThread?: () => void;
    onClose?: () => void;
};

export const SwingThread = forwardRef<HTMLDivElement, SwingThreadProps>(
    (
        {
            threadId,
            title,
            nodeId,
            initialPositionX,
            initialPositionY,
            currentUserId,
            swingUuid,
            options,
            fromFrame,
            activeUserId,
            messages = [],
            onPostUpsertThread,
            onClose,
        },
        ref,
    ) => {
        useSwingThreadMessagesSubscription(threadId);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
        const [editMainComment, setEditMainComment] = useState(false);
        const [isSelectingParameter, toggleSelectingParameters] = useToggle(false);
        const { data: unreadNotifications } = useThreadMessageUnreadNotifications();
        const { mutateAsync: deleteMessage } = useDeleteThreadMessage();
        const { mutateAsync: deleteThread } = useDeleteSwingThread();
        const { mutateAsync: sendMessage } = useSendMessage();
        const { mutateAsync: upsertThread } = useUpsertSwingThread();
        const { mutateAsync: markAllAsRead } = useMarkNotificationAsRead(activeUserId);

        useEffect(() => {
            if (threadId && !!unreadNotifications?.threads?.[threadId]?.length) {
                markAllAsRead(unreadNotifications?.threads?.[threadId]);
            }
        }, [threadId, markAllAsRead, unreadNotifications?.threads]);

        const handleEnableMainCommentEdit = useCallback(() => setEditMainComment(true), []);
        const handleFormCancel = useCallback(() => {
            setEditMainComment(false);
        }, []);

        const handleDeleteThread = useCallback(() => {
            if (!threadId || !swingUuid) return;
            deleteThread({ id: threadId, swingUuid });
        }, [deleteThread, swingUuid, threadId]);

        const handleUpsertThread = useCallback(
            async ({ nodeId, message, messageId }: SwingThreadSubmitPayload) => {
                if (!swingUuid) return;

                const swingThread = await upsertThread({
                    linkNodeId: nodeId ?? null,
                    fromFrame,
                    swingUuid,
                    id: threadId,
                });

                if (swingThread) {
                    sendMessage({ threadId: swingThread.id, message: message, id: messageId });
                }
                onPostUpsertThread?.();
            },
            [fromFrame, onPostUpsertThread, sendMessage, swingUuid, threadId, upsertThread],
        );

        const handleUpsertMessage = useCallback(
            ({ message, id, threadId }: SwingThreadMessageSubmitPayload) => {
                sendMessage({ message, id, threadId });
            },
            [sendMessage],
        );

        const handleDeleteMessage = useCallback(
            ({ id, threadId }: SwingThreadMessageDeletePayload) => {
                deleteMessage({ id, threadId });
            },
            [deleteMessage],
        );
        const hasMessages = messages.length > 0;
        const shouldDisplayMessage = !editMainComment && threadId && hasMessages && currentUserId;
        return (
            <>
                <SwingThreadContainer
                    ref={ref}
                    initialPositionX={initialPositionX}
                    initialPositionY={initialPositionY}
                    headerNode={
                        <>
                            <RenderTitle
                                isSelectingParameter={isSelectingParameter}
                                title={title}
                                editMainComment={editMainComment}
                                hasMessages={hasMessages}
                                toggleParameterView={toggleSelectingParameters}
                            />
                            <button onClick={onClose}>
                                <CircularCloseIcon color={colors.teal[700]} />
                            </button>
                        </>
                    }
                >
                    {(!hasMessages || editMainComment) && (
                        <SwingThreadForm
                            nodeId={nodeId}
                            isSelectingParameter={isSelectingParameter}
                            options={options}
                            selectedOption={title}
                            message={messages?.[0]?.message}
                            messageId={messages?.[0]?.id}
                            isEditing={editMainComment}
                            onCancel={handleFormCancel}
                            onDeleteThread={() => setIsDeleteModalOpen(true)}
                            onToggleParameterView={toggleSelectingParameters}
                            onSubmitForm={handleUpsertThread}
                        />
                    )}
                    {shouldDisplayMessage && (
                        <SwingThreadMessages
                            currentUserId={currentUserId}
                            threadId={threadId}
                            messages={messages}
                            onUpsert={handleUpsertMessage}
                            onEditMainComment={handleEnableMainCommentEdit}
                            onDelete={handleDeleteMessage}
                        />
                    )}
                </SwingThreadContainer>
                {isDeleteModalOpen && (
                    <ConfirmDeleteThreadModal
                        onDismiss={() => setIsDeleteModalOpen(false)}
                        onSubmit={handleDeleteThread}
                    />
                )}
            </>
        );
    },
);

SwingThread.displayName = 'SwingThreads';

type RenderTitleProps = {
    title?: string;
    isSelectingParameter?: boolean;
    editMainComment?: boolean;
    hasMessages?: boolean;
    toggleParameterView: () => void;
};

const RenderTitle = ({
    isSelectingParameter,
    title,
    toggleParameterView,
    hasMessages,
    editMainComment,
}: RenderTitleProps) => {
    if (isSelectingParameter) {
        return (
            <Box
                role="button"
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={u(1)}
                onClick={toggleParameterView}
            >
                <CaretLeftIcon size={u(3)} color={colors.teal[700]} /> <span>Select parameter</span>
            </Box>
        );
    }

    if (hasMessages && !editMainComment) {
        return (
            <Box display="flex" alignItems="center" style={{ marginLeft: -1 * u(2) }}>
                <HandleVerticalIcon width={u(2)} height={u(2)} color={colors.teal[700]} />
                <h4>{title}</h4>
            </Box>
        );
    }

    return <h4>{editMainComment ? 'Edit Note' : 'Create Note'}</h4>;
};
